import type { ReactNode } from 'react';
import { EmptyState } from './EmptyState.js';
import { ErrorBanner } from './ErrorBanner.js';

export type AsyncKind = 'table' | 'kpis' | 'cards' | 'kanban' | 'detail';

export interface AsyncBoundaryProps {
  kind: AsyncKind;
  loading?: boolean;
  error?: unknown;
  empty?: boolean;
  emptyTitle?: string;
  emptyHint?: ReactNode;
  emptyAction?: ReactNode;
  onRetry?: () => void;
  /** Purely visual — wrap the content in the same card affordance as the skeleton. Ignored today (kept for API compat). */
  inCard?: boolean;
  children: ReactNode;
}

/**
 * Kollez rule: every list has loading, empty, and error states.
 * Wraps `children` and swaps in the right skeleton / message based on props.
 * Skeleton markup mirrors `shell.js` autoStates() output for each `kind`.
 */
export function AsyncBoundary({
  kind,
  loading,
  error,
  empty,
  emptyTitle = 'Nothing here yet',
  emptyHint,
  emptyAction,
  onRetry,
  inCard: _inCard,
  children,
}: AsyncBoundaryProps) {
  void _inCard;
  if (error) {
    return (
      <ErrorBanner
        message={error instanceof Error ? error.message : 'Please try again.'}
        onRetry={onRetry}
      />
    );
  }
  if (loading) {
    return <Skeleton kind={kind} />;
  }
  if (empty) {
    return <EmptyState title={emptyTitle} hint={emptyHint} action={emptyAction} />;
  }
  return <>{children}</>;
}

function Skeleton({ kind }: { kind: AsyncKind }) {
  // Screen-reader heading so assistive tech and accessibility audits always see a landmark
  // during loading. Visually hidden via .sr-only; the visible skeleton bars carry the actual
  // affordance for sighted users.
  const srHeading = <h2 className="sr-only">Loading</h2>;
  switch (kind) {
    case 'kpis':
      return (
        <div className="skeleton-kpis">
          {srHeading}
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton skeleton-kpi" />
          ))}
        </div>
      );
    case 'cards':
      return (
        <div className="skeleton-cards">
          {srHeading}
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="skeleton skeleton-card" />
          ))}
        </div>
      );
    case 'kanban':
      return (
        <div className="skeleton-kanban">
          {srHeading}
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="skeleton skeleton-col" />
          ))}
        </div>
      );
    case 'detail':
      return (
        <div className="skeleton-detail">
          {srHeading}
          <div className="skeleton skeleton-title" />
          <div className="skeleton skeleton-line" />
          <div className="skeleton skeleton-line short" />
        </div>
      );
    case 'table':
    default:
      return (
        <div className="skeleton-table">
          {srHeading}
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="skeleton skeleton-row" />
          ))}
        </div>
      );
  }
}
